import { mergeHeadToHeadGames } from '@shared/helpers/head-to-head-helpers';
import { generateH2HCollectionsByGamesWithScoreIDs } from '@shared/helpers';
import { HeadToHeadFirebaseCollection, HeadToHeadID, SeasonID } from '@shared/types';
import { fetchGameWithScoreIDsBySeasonID } from 'api/admin/AdminTeamResultsAPI';
import { Cls, Emojis } from 'app/constants';
import { collection, deleteField, doc, getDocs, runTransaction, serverTimestamp, Timestamp, writeBatch } from 'firebase/firestore';
import { updatePartialData } from 'lib/idb/IndexDb';
import { Stores } from 'types/IndexDb.types';
import { db } from '../../lib/firebase';

const PATH = 'headtohead';

/**
 * Merges head-to-head results by game ID and rebuilds totals from the unique history.
 * Incoming games replace earlier versions of the same game, while other seasons remain.
 * Each matchup is read and written in its own transaction so concurrent runs cannot
 * overwrite one another's history. Firestore retries a transaction if its read changes.
 * Legacy literal dotted counter fields are removed; allTimeRecord is a nested map.
 * A season is not atomic across matchups: a failed run can safely be rerun to finish.
 *
 * @param {Record<HeadToHeadID, HeadToHeadFirebaseCollection>} h2hGames - Record of H2H matchups keyed by HeadToHeadID
 *
 * @return {Promise<HeadToHeadFirebaseCollection[]>} Committed matchup data, excluding the server timestamp sentinel
 *
 * @example
 * const h2hGames = {
 *   '01-ARI_02-ATL': {
 *     headToHeadID: '01-ARI_02-ATL',
 *     team1AbrvID: '01-ARI',
 *     team2AbrvID: '02-ATL',
 *     allTimeRecord: { team1Wins: 2, team2Wins: 0, ties: 0 },
 *     games: [...]
 *   }
 * };
 * await writeHeadToHeadGameResults(h2hGames);
 */
export const writeHeadToHeadGameResults = async (
  h2hGames: Record<HeadToHeadID, HeadToHeadFirebaseCollection>,
): Promise<HeadToHeadFirebaseCollection[]> => {
  const committedRecords: HeadToHeadFirebaseCollection[] = [];
  const countOfH2HMatchups = Object.keys(h2hGames).length;
  for (const [headToHeadID, incoming] of Object.entries(h2hGames)) {
    const matchupRef = doc(db, PATH, headToHeadID);
    const committedRecord = await runTransaction(db, async (transaction) => {
      const snapshot = await transaction.get(matchupRef);
      const existing = snapshot.data() as HeadToHeadFirebaseCollection | undefined;
      const record: HeadToHeadFirebaseCollection = {
        ...mergeHeadToHeadGames(headToHeadID as HeadToHeadID, existing?.games ?? [], incoming.games),
        lastUpdated: Date.now(),
      };
      transaction.set(
        matchupRef,
        {
          ...record,
          // In set data these are literal legacy field names, not nested field paths.
          'allTimeRecord.team1Wins': deleteField(),
          'allTimeRecord.team2Wins': deleteField(),
          'allTimeRecord.ties': deleteField(),
          serverTimestamp: serverTimestamp(),
        },
        { merge: true },
      );
      // Return data only from the committed attempt. Firestore transform sentinels
      // cannot be stored in IndexedDB; lastUpdated is shared by both copies.
      return record;
    });
    committedRecords.push(committedRecord);
  }

  const msg = `${Emojis.rocket} %c WROTE %c %c ${countOfH2HMatchups} %c H2H matchups`;
  console.log(msg, Cls.Act, '', Cls.Val, '');
  return committedRecords;
};

/**
 * Fetches all head-to-head records from Firestore.
 * Returns a record keyed by HeadToHeadID for efficient lookup.
 * Normalizes Firestore timestamps to ISO strings before caching or serialization.
 *
 * @return {Promise<Record<HeadToHeadID, HeadToHeadFirebaseCollection>>} Record of all head-to-head matchups
 *
 * @example
 * const h2hRecords = await fetchHeadToHeadGameResults();
 * const ariVsAtl = h2hRecords['01-ARI_02-ATL'];
 * console.log(ariVsAtl.allTimeRecord); // { team1Wins: 2, team2Wins: 0, ties: 0 }
 */
export const fetchHeadToHeadGameResults = async (): Promise<Record<HeadToHeadID, HeadToHeadFirebaseCollection>> => {
  console.log(`${Emojis.eyes} Fetching H2H from %c ${PATH} %c`, Cls.Loc, '');

  try {
    const collectionRef = collection(db, PATH);
    const snapshot = await getDocs(collectionRef);

    const h2hRecords: Record<HeadToHeadID, HeadToHeadFirebaseCollection> = {};
    snapshot.forEach((doc) => {
      const { serverTimestamp, ...record } = doc.data();
      const timestamp = serverTimestamp instanceof Timestamp
        ? serverTimestamp.toDate().toISOString()
        : typeof serverTimestamp === 'string' ? serverTimestamp : undefined;
      h2hRecords[doc.id as HeadToHeadID] = {
        ...record,
        ...(timestamp !== undefined ? { serverTimestamp: timestamp } : {}),
      } as HeadToHeadFirebaseCollection;
    });

    console.log(`${Emojis.eyes} Fetched %c ${Object.keys(h2hRecords).length} %c H2H records`, Cls.Val, '');
    return h2hRecords;
  } catch (error) {
    console.error(`${Emojis.ex} Error fetching head to head: `, error);
    return {};
  }
};

/**
 * Generates head-to-head game results for a specific season.
 * Fetches game scores, generates matchup records, and writes to Firestore.
 * Upserts the committed histories into IndexedDB before reporting completion.
 * A local write failure rejects the job; rerunning safely repeats the Firestore merge.
 *
 * @param {SeasonID} seasonID - The season year (e.g., '2025')
 * @param {VoidFunction} [callbackFn] - Optional callback function to execute after completion
 *
 * @return {Promise<void>}
 *
 * @example
 * await generateHeadToHeadGamesBySeasonID('2025', () => {
 *   console.log('Head-to-head generation complete!');
 * });
 */
export const generateHeadToHeadGamesBySeasonID = async (
  seasonID: SeasonID,
  callbackFn?: VoidFunction,
): Promise<void> => {
  const FUNC_ID = 'generateHeadToHeadGamesBySeasonID';
  console.log(`${FUNC_ID} Generating Head-to-Head games for seasonID %c ${seasonID} %c`, Cls.ValLg, '');

  const gamesWithScores = await fetchGameWithScoreIDsBySeasonID(seasonID);
  const h2hCollections = generateH2HCollectionsByGamesWithScoreIDs(gamesWithScores);

  const committedRecords = await writeHeadToHeadGameResults(h2hCollections);
  // This season covers only some matchup pairs, not the full H2H collection.
  await updatePartialData(Stores.HeadToHead, committedRecords);

  console.log(`${FUNC_ID} Completed generating Head-to-Head games for seasonID %c ${seasonID} %c`, Cls.ValLg, '');

  callbackFn?.();
  return;
};

/**
 * Re-sorts the games arrays in all head-to-head documents by seasonWeekID.
 * Useful after backfilling historical data or when games were added out of order.
 * Sorts in ascending order (oldest to newest).
 *
 * @return {Promise<void>}
 *
 * @example
 * // After adding 2022 games to existing 2024-2025 records
 * await reSortHeadToHeadGames();
 * // Games array now sorted: ['2022-01', '2022-02', ..., '2025-17']
 */
export const reSortHeadToHeadGames = async (): Promise<void> => {
  const h2hCollection = collection(db, PATH);
  const snapshot = await getDocs(h2hCollection);

  const batch = writeBatch(db);
  let updateCount = 0;

  snapshot.docs.forEach((docSnap) => {
    const data = docSnap.data() as HeadToHeadFirebaseCollection;

    if (data.games && data.games.length > 0) {
      const sortedGames = [...data.games].sort((a, b) => {
        return a.seasonWeekID.localeCompare(b.seasonWeekID);
      });

      // Update document with sorted games
      const docRef = doc(db, PATH, docSnap.id);
      batch.update(docRef, { games: sortedGames });
      updateCount++;
    }
  });

  await batch.commit();
  console.log(`✅ Resorted ${updateCount} head-to-head matchups`);
};

/**
 * Rebuilds head-to-head totals from unique game history and removes flat legacy counters.
 * Uses the same transactional writer as season generation; already-correct documents
 * remain correct when this maintenance action is repeated.
 *
 * @return {Promise<void>}
 *
 * @example
 * await fixHeadToHeadStructure();
 */
export const fixHeadToHeadStructure = async (): Promise<void> => {
  const h2hCollection = collection(db, PATH);
  const snapshot = await getDocs(h2hCollection);
  for (const docSnap of snapshot.docs) {
    const data = docSnap.data() as HeadToHeadFirebaseCollection;
    // Read the latest games inside the transaction rather than replaying this
    // collection snapshot over a concurrent season generation.
    await writeHeadToHeadGameResults({
      [docSnap.id]: { ...data, games: [] },
    } as Record<HeadToHeadID, HeadToHeadFirebaseCollection>);
  }

  console.log('✅ Fixed allTimeRecord structure');
};
